import React, { useEffect, useState } from 'react';
import Menu from 'components/QuickTurn/Menu';
import { Button, Card, Col, Divider, Row, Descriptions, Typography, Breadcrumb, Tag } from 'antd';
import { Link, useParams } from 'react-router-dom';
import QuickTurnPartStore from 'stores/QuickTurnPartStore';
import dayjs from 'dayjs';
import { Price, ThreeJSThumbnail } from '@partsbadger/library';
import { observer } from 'mobx-react-lite';

import { QuickTurnSalesOrder, QuickTurnSalesOrderLineItem, QuickTurnSalesOrderShipment } from '@partsbadger/types';

const { Title } = Typography;

const OrderDetails = () => {
    const [orderData, setOrderData] = useState<QuickTurnSalesOrder>();
    const { id } = useParams<any>();

    async function fetchOrder() {
        const response = await QuickTurnPartStore.getCompletedSalesOrderById(id);

        setOrderData(response);
    }

    useEffect(() => {
        fetchOrder();
    }, []);

    return (
        <>
            <Menu title="Order Details" />

            <div className="container">
                <Breadcrumb>
                    <Breadcrumb.Item>
                        <Link to="/quick-turn/design">QuickTurn</Link>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>
                        <Link to="/quick-turn/order-history">Order History</Link>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>Order Details</Breadcrumb.Item>
                </Breadcrumb>

                <div className="d-flex flex-column mt-4">
                    <div className="d-flex flex-row align-items-center mb-2">
                        <Title level={2} className="mb-0 mr-3">
                            {orderData?.name}
                        </Title>
                        <Tag color={orderData?.status !== 'Cancelled' ? 'green' : 'red'}>{orderData?.status}</Tag>
                    </div>
                    <div className="">
                        <span>Placed on: </span>
                        <span>{dayjs(orderData?.payment_date).format('MM/DD/YYYY')}</span>
                    </div>
                </div>

                <Card className="container mt-3 shadow-sm">
                    <Row gutter={[8, 0]}>
                        <Col xs={24} md={8}>
                            <div className="flex flex-col">
                                <p className="text-black font-bold">Shipping Address</p>

                                <span className="font-size-base">{orderData?.shipping_name}</span>
                                <span className="font-size-base">{`${orderData?.shipping_street}`}</span>
                                <span className="font-size-base">
                                    {`${orderData?.shipping_city}, ${orderData?.shipping_state}, ${orderData?.shipping_zip_code}`}
                                </span>
                            </div>
                        </Col>
                        <Col xs={24} md={10}>
                            <div className="flex flex-col">
                                <p className="text-black font-bold">Payment Method </p>

                                <span className="font-size-base">Credit Card </span>
                                <span className="font-size-base">
                                    {orderData?.payment_details?.card_brand} **** **** ****{' '}
                                    {orderData?.payment_details?.last_4}
                                </span>
                            </div>
                        </Col>
                        <Col xs={24} md={6}>
                            <div className="flex flex-col">
                                <p className="text-black font-bold">Order Totals</p>
                                <span className="flex justify-between">
                                    <span className="font-weight-semibold">Subtotal:</span>{' '}
                                    <Price value={orderData?.subtotal ? orderData?.subtotal : 0} />
                                </span>
                                {orderData?.discount && orderData.discount !== '0.00' ? (
                                    <div className="d-flex font-size-base justify-content-between">
                                        <span className="font-weight-semibold">Discount:</span>
                                        <span>
                                            -<Price value={Number(orderData?.discount)} />
                                        </span>
                                    </div>
                                ) : null}
                                <span className="flex justify-between">
                                    <span className="font-weight-semibold">Shipping:</span>{' '}
                                    <Price value={orderData?.shipping ? orderData?.shipping : 0} />
                                </span>
                                <span className="flex justify-between">
                                    <span className="font-weight-semibold">Tax:</span>{' '}
                                    <Price value={orderData?.tax ? orderData?.tax : 0} />
                                </span>
                                <span className="flex justify-between mt-2">
                                    <span className="font-bold text-black">Total:</span>{' '}
                                    <Price
                                        style={{ color: '#000', fontWeight: 'bold' }}
                                        value={orderData?.grand_total ? orderData?.grand_total : 0}
                                    />
                                </span>
                            </div>
                        </Col>
                    </Row>
                    {orderData?.shipments?.length ? (
                        <>
                            <Divider />
                            <Row>
                                <Col xs={24} md={12}>
                                    <p className="text-black font-bold">Shipping Information</p>
                                    {orderData?.shipments.map((shipment: QuickTurnSalesOrderShipment) => {
                                        return (
                                            <div className="d-flex flex-row" key={shipment.id}>
                                                <div className="flex flex-col mr-5">
                                                    <span className="font-weight-semibold">Carrier</span>{' '}
                                                    <span>{shipment.carrier}</span>
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="font-weight-semibold">Tracking Number</span>{' '}
                                                    <span>{shipment.tracking_number}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </Col>
                            </Row>
                        </>
                    ) : null}
                </Card>
                <Title level={2}>Order Items</Title>
                <Card className="mt-3 shadow-sm" bodyStyle={{ padding: 0 }}>
                    <div className="p-3 w-100">
                        {orderData?.line_items?.map((item: QuickTurnSalesOrderLineItem, index: number) => (
                            <>
                                {index !== 0 ? <Divider /> : null}

                                <div key={item.id} className="mb-4">
                                    <div className="d-flex flex-row">
                                        <div className="mr-3">
                                            <ThreeJSThumbnail
                                                id={item.part.id}
                                                size="75px"
                                                edges={false}
                                                background="#ffffff"
                                            />
                                        </div>
                                        <div className="d-flex flex-sm-row flex-column justify-content-md-between align-items-md-center w-100">
                                            <div className="d-flex flex-column">
                                                <div className="d-flex flex-column">
                                                    <span className="font-size-md mb-2">{item.part.name}</span>
                                                    <div>
                                                        <span className="mr-2">
                                                            <span className="font-weight-semibold">Qty:</span>{' '}
                                                            <span>{item.part.quantity}</span>
                                                        </span>
                                                        <span>
                                                            <span className="font-weight-semibold">Material:</span>{' '}
                                                            <span>{`${item.part.material.type} ${item.part.material.alloy}`}</span>
                                                        </span>
                                                    </div>
                                                    <Divider className="mb-1 mt-1" />
                                                    <div className="d-flex flex-sm-row flex-column justify-between mt-2">
                                                        <div className="d-flex flex-column">
                                                            <span>
                                                                <span className="font-weight-semibold">
                                                                    Overall Length:
                                                                </span>{' '}
                                                                <span>{`${item.part.overallLength}"`}</span>
                                                            </span>

                                                            <span>
                                                                <span className="font-weight-semibold">
                                                                    Outer Diameter:
                                                                </span>{' '}
                                                                <span>{`${item.part.outerDiameter}"`}</span>
                                                            </span>

                                                            <span>
                                                                <span className="font-weight-semibold">Chamfer:</span>{' '}
                                                                <span>{`${item.part.chamferWidth}"`}</span>
                                                            </span>
                                                        </div>
                                                        <div className="d-flex flex-column md:ml-5">
                                                            {item.part.hasFlange && (
                                                                <>
                                                                    <span>
                                                                        <span className="font-weight-semibold">
                                                                            Flange Diameter:
                                                                        </span>{' '}
                                                                        <span>{`${item.part.flangeDiameter}`}</span>
                                                                    </span>
                                                                    <span>
                                                                        <span className="font-weight-semibold">
                                                                            Flange Length:
                                                                        </span>{' '}
                                                                        <span>{`${item.part.flangeThickness}`}</span>
                                                                    </span>
                                                                </>
                                                            )}
                                                            {item.part.hasThruHole && (
                                                                <span>
                                                                    <span className="font-weight-semibold">
                                                                        Thru-Hole Diameter:
                                                                    </span>{' '}
                                                                    <span>{`${item.part.innerDiameter}"`}</span>
                                                                </span>
                                                            )}

                                                            {item.part.hasFrontCountersink && (
                                                                <>
                                                                    <span>
                                                                        <span className="font-weight-semibold">
                                                                            Countersink Angle:
                                                                        </span>{' '}
                                                                        <span>{`${item.part.frontCountersinkAngle} deg.`}</span>
                                                                    </span>
                                                                    <span>
                                                                        <span className="font-weight-semibold">
                                                                            Countersink Depth:
                                                                        </span>{' '}
                                                                        <span>{`${item.part.frontCountersinkDepth}"`}</span>
                                                                    </span>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="d-flex flex-column justify-content-center align-items-start md:ml-3 mb-xl-3 mt-3">
                                                <span
                                                    style={{
                                                        fontSize: '1rem',
                                                        fontWeight: 400,
                                                    }}
                                                >
                                                    <Price value={item.unit_price} /> ea.
                                                </span>
                                                <span
                                                    style={{
                                                        fontSize: '1.2rem',
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    <Price value={item.total_price} />
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        ))}
                    </div>
                </Card>
            </div>
        </>
    );
};

export default observer(OrderDetails);
